/* ============================================================================
   CreationCanvas — composant central du one-pager Marthe v2.

   Pipelines :
     - mode 'image-to-ascii' : params.imageSrc (data URL) → ASCII grid string[][]
     - mode 'procedural' : seed + ornamentStyle + symmetry → ASCII grid
     - mode 'marthe' : Gemini-generated image → glyph-art ASCII

   Render : `<pre>` font mono, transformations CSS pilotees par hand tracking.
   Architecture animation :
     - mediapipe.ts publie HandFrame ~30 Hz, lisses par One Euro Filter
     - on stocke le frame dans un ref (lecture O(1) sans re-render)
     - une boucle rAF unique lit le ref, compute les 7 mappings, mute
       directement preRef.current.style.transform / .filter
     - le state React handFrame est throttle a 10 Hz pour la legende live
       et les indicators (pas pour les transforms)
     - lerp framerate-independant : t = 1 - exp(-rate * dt)

   7 mappings supports :
     1. pinch 2 mains       → scatter
     2. index pointe        → translate sur le doigt
     3. pinch droit serre   → multiplie N copies
     4. pinch gauche serre  → zoom-in continu
     5. swipe → main droite → cycle ornament
     6. poing ferme         → freeze + reset position
     7. no hands            → drift sinusoidal ambient
   ============================================================================ */

import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  useCanvasParams,
  useHandTracking,
  type CanvasParams,
  type HandFrame,
  type HandLandmark,
  type OrnamentStyleId,
} from '../../lib/canvasStore';
import { decodeImage, imageToAsciiGrid } from '../../lib/ascii/imageToAscii';
import { StippleLayer } from './StippleLayer';
import { HalftoneLayer } from './HalftoneLayer';
import { LiquidLayer } from './LiquidLayer';
import { GalleryGrid } from './GalleryGrid';
import { FuckedUpLayer } from './FuckedUpLayer';
import { generateProceduralAscii } from '../../lib/ascii/procedural';
import { renderAsciiToString } from '../../lib/ascii/render';
import { usePolledMessages } from '../../lib/usePolledMessages';
import {
  publishGalleryIndex,
  useGalleryCommands,
} from '../../lib/galleryBus';
import type { HandTracker as HandTrackerT } from '../../lib/handtracking/mediapipe';
import {
  computePinch,
  isClap,
  isFingerHeart,
  isFist,
  isIndexPointing,
  isPeaceSign,
  palmCentroid,
  partitionHands,
} from '../../lib/handtracking/gestures';
import { ParticleSystem } from '../../lib/handtracking/particles';
import { getFeatureFlagsCached, type FeatureFlags } from '../../lib/featureFlags';
import { createFrameLogger, type FrameLogger } from '../../lib/perf/FrameLogger';
import type { FluidLayerHandle } from './FluidLayer';
import type { FaceLayerHandle } from './FaceLayer';
import type { PresenceOverlayHandle } from './PresenceOverlay';
// Phase B2 — FluidLayer chargé en lazy chunk si flags.fluidSim = true.
// Ne pollue pas le bundle main quand le flag est OFF (default).
const FluidLayer = lazy(() =>
  import('./FluidLayer').then((m) => ({ default: m.FluidLayer })),
);
// Mode 'face' — FaceLayer chargé en lazy chunk si params.mode === 'face'.
// Le modèle MediaPipe FaceLandmarker pèse ~3.7MB, lazy-loaded only when needed.
const FaceLayer = lazy(() =>
  import('./FaceLayer').then((m) => ({ default: m.FaceLayer })),
);
// Mode 'fairy-world' — monde ouvert Three.js (~170KB gzip three + moteur).
// Lazy chunk : ne pèse rien tant qu'on n'entre pas dans le monde des fées.
const FairyWorldLayer = lazy(() =>
  import('./FairyWorldLayer').then((m) => ({ default: m.FairyWorldLayer })),
);
// Mode 'face2' (présence) — PresenceOverlay chargé en lazy chunk si
// params.mode === 'face2'. Modèle BlazeFace short-range ~230KB, plus léger
// que FaceLandmarker (468 lm).
const PresenceOverlay = lazy(() =>
  import('./PresenceOverlay').then((m) => ({ default: m.PresenceOverlay })),
);
import './canvas.css';

/* ----------------------------------------------------------------------------
   Quick Wins (Phase A) tunables — modulation dynamique du multiply
   ---------------------------------------------------------------------------- */

/** Vélocité palm normalisée [unit/s] qui produit le boost alpha max (A1). */
const QW_MAX_VELOCITY = 1.5;
/** Alpha de base des copies multiply (palier bas). */
const QW_BASE_ALPHA = 0.55;
/** Alpha boost max par vélocité (atteint à QW_MAX_VELOCITY). */
const QW_ALPHA_BOOST = 0.30;
/** Taux de lerp velocity (plus haut = plus réactif, moins lissé). */
const QW_VELOCITY_LERP = 18;
/** Taux de lerp Z palm (plus bas = lissage doux, anti-bruit MediaPipe Z). */
const QW_PALMZ_LERP = 6;
/** Multiplicateur Z palm → offset px parallax copies (A5). */
const QW_PALMZ_PX = 100;
/** Threshold base pour activer multiply (modulé par confidence en A4). */
const QW_MULTIPLY_BASE_THRESHOLD = 0.3;
/** Boost confidence : threshold = base - boost * confidence. */
const QW_MULTIPLY_CONF_BOOST = 0.2;

/* ----------------------------------------------------------------------------
   Helpers — debounce
   ---------------------------------------------------------------------------- */

function useDebouncedValue<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

/* ----------------------------------------------------------------------------
   Constantes mapping
   ---------------------------------------------------------------------------- */

const ORNAMENT_CYCLE: OrnamentStyleId[] = [
  'baroque-scroll',
  'floral-wreath',
  'vine-border',
  'arabesque-mirror',
];

/** Cooldown swipe — 600ms entre 2 declenchements. */
const SWIPE_COOLDOWN_MS = 600;
/** Seuil de variation X minimum pour declencher un swipe. */
const SWIPE_DELTA = 0.06;

/** Throttle React state setFrame a 10 Hz (au lieu de 30 Hz tracker). */
const SET_FRAME_THROTTLE_MS = 100;

/** Lerp rate (par seconde) framerate-independant : valeur converge a ~95% en 200ms a rate=15. */
const LERP_RATE = 18;

/* ----------------------------------------------------------------------------
   Active-gestures snapshot (legend live)
   ---------------------------------------------------------------------------- */

interface ActiveGestures {
  scatterFromHands: boolean; // pinch des 2 mains (serres ensemble)
  indexPointing: boolean;
  pinchRightTight: boolean; // pinch droit serre (pinch < 0.5)
  pinchLeftTight: boolean; // pinch gauche serre (pinch < 0.5)
  swiping: boolean; // un swipe vient de se declencher (visuel pulse)
  fist: boolean;
  noHands: boolean;
  peaceSign: boolean; // Y / V sign → emit etoiles magiques
  fingerHeart: boolean; // K-pop finger heart → emit coeurs flottants
}

const EMPTY_ACTIVE: ActiveGestures = {
  scatterFromHands: false,
  indexPointing: false,
  pinchRightTight: false,
  pinchLeftTight: false,
  swiping: false,
  fist: false,
  noHands: true,
  peaceSign: false,
  fingerHeart: false,
};

/* ----------------------------------------------------------------------------
   Composant
   ---------------------------------------------------------------------------- */

export function CreationCanvas() {
  const [params, dispatch] = useCanvasParams();
  const { frame: handFrame, setFrame } = useHandTracking();

  /* ---- Image decode (cache) ---- */
  const [imageData, setImageData] = useState<ImageData | null>(null);
  const [decodeError, setDecodeError] = useState<string | null>(null);
  const [decoding, setDecoding] = useState(false);

  /* ---- Gallery state (mode = 'gallery') ---- */
  const galleryActive = params.mode === 'gallery';
  /* ---- Mode 'fluid' (déclaré ici pour piloter le polling messages) ---- */
  const fluidActiveMode = params.mode === 'fluid';
  // Polling rapide (4s) en gallery + fluid (perturbation chat live), sinon 30s
  // Polling 5s partout — compromis ressenti "live chat" / quota Cloudflare
  // workers.dev (~100k req/jour free). 2s × multiples onglets explosait.
  const polled = usePolledMessages(5000);
  const galleryPhotos = useMemo(
    () =>
      galleryActive
        ? polled.messages.filter((m) => !!m.photoUrl && m.verified)
        : [],
    [galleryActive, polled.messages],
  );
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryImageData, setGalleryImageData] = useState<ImageData | null>(null);
  const [galleryDecoding, setGalleryDecoding] = useState(false);
  const [galleryDecodeError, setGalleryDecodeError] = useState<string | null>(null);

  // Re-clamp index si la liste retrecit
  useEffect(() => {
    if (!galleryActive) return;
    if (galleryPhotos.length === 0) {
      if (galleryIndex !== 0) setGalleryIndex(0);
      return;
    }
    if (galleryIndex >= galleryPhotos.length) {
      setGalleryIndex(0);
    }
  }, [galleryActive, galleryPhotos.length, galleryIndex]);

  // Publie l'index courant pour la sidebar
  useEffect(() => {
    if (!galleryActive) return;
    publishGalleryIndex(galleryIndex);
  }, [galleryActive, galleryIndex]);

  // Auto-rotate timer
  useEffect(() => {
    if (!galleryActive) return;
    const ms = params.galleryAutoRotateMs;
    if (!ms || galleryPhotos.length <= 1) return;
    const id = setInterval(() => {
      setGalleryIndex((i) => (i + 1) % galleryPhotos.length);
    }, ms);
    return () => clearInterval(id);
  }, [galleryActive, params.galleryAutoRotateMs, galleryPhotos.length]);

  // Decode photo courante
  useEffect(() => {
    if (!galleryActive) {
      setGalleryImageData(null);
      setGalleryDecodeError(null);
      return;
    }
    const photo = galleryPhotos[galleryIndex];
    if (!photo?.photoUrl) {
      setGalleryImageData(null);
      setGalleryDecodeError(null);
      return;
    }
    let cancelled = false;
    setGalleryDecoding(true);
    decodeImage(photo.photoUrl)
      .then((img) => {
        if (cancelled) return;
        setGalleryImageData(img);
        setGalleryDecodeError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setGalleryImageData(null);
        setGalleryDecodeError(e?.message ?? String(e));
      })
      .finally(() => {
        if (!cancelled) setGalleryDecoding(false);
      });
    return () => {
      cancelled = true;
    };
  }, [galleryActive, galleryPhotos, galleryIndex]);

  // Listener commandes prev/next/set depuis la sidebar (et la mini barre)
  const onGalleryCommand = useCallback(
    (detail: { cmd: 'prev' | 'next' | 'set'; index?: number }) => {
      const total = galleryPhotos.length;
      if (total === 0) return;
      if (detail.cmd === 'prev') {
        setGalleryIndex((i) => (i - 1 + total) % total);
      } else if (detail.cmd === 'next') {
        setGalleryIndex((i) => (i + 1) % total);
      } else if (detail.cmd === 'set' && typeof detail.index === 'number') {
        setGalleryIndex(Math.max(0, Math.min(total - 1, detail.index)));
      }
    },
    [galleryPhotos.length],
  );
  useGalleryCommands(onGalleryCommand);

  useEffect(() => {
    let cancelled = false;
    const needsDecode =
      params.mode === 'image-to-ascii' || params.mode === 'marthe';
    if (!needsDecode) {
      setImageData(null);
      setDecodeError(null);
      return;
    }
    if (!params.imageSrc) {
      setImageData(null);
      setDecodeError(null);
      return;
    }
    setDecoding(true);
    decodeImage(params.imageSrc)
      .then((img) => {
        if (cancelled) return;
        setImageData(img);
        setDecodeError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setImageData(null);
        setDecodeError(e?.message ?? String(e));
      })
      .finally(() => {
        if (!cancelled) setDecoding(false);
      });
    return () => {
      cancelled = true;
    };
  }, [params.imageSrc, params.mode]);

  /* ---- Debounce des params qui re-trigger le grid ---- */
  const debouncedParams = useDebouncedValue(params, 100);

  const grid: string[][] = useMemo(() => {
    if (debouncedParams.mode === 'image-to-ascii') {
      if (!imageData) return [];
      return imageToAsciiGrid(imageData, {
        glyphSet: debouncedParams.glyphSet,
        density: debouncedParams.density,
        contrast: debouncedParams.contrast,
        brightness: debouncedParams.brightness,
        invert: debouncedParams.invert,
        ditherAmount: debouncedParams.ditherAmount,
      });
    }
    if (debouncedParams.mode === 'marthe') {
      // Mode marthe : tous les params de rendu (glyphSet, palette, etc.) sont
      // customisables comme en mode image. whitespaceCutoff=0.92 → les pixels
      // quasi-blancs deviennent des espaces (fond invisible).
      if (imageData) {
        return imageToAsciiGrid(imageData, {
          glyphSet: debouncedParams.glyphSet,
          density: debouncedParams.density,
          contrast: debouncedParams.contrast,
          brightness: debouncedParams.brightness,
          invert: debouncedParams.invert,
          ditherAmount: debouncedParams.ditherAmount,
          whitespaceCutoff: 0.92,
        });
      }
      // Fallback procedural quand pas encore d'image — preset signature
      // (floral wreath + fairy + horizontal). L'user peut quand même changer
      // le glyphSet via la sidebar : on utilise debouncedParams.glyphSet.
      return generateProceduralAscii({
        glyphSet: debouncedParams.glyphSet,
        density: debouncedParams.density,
        invert: debouncedParams.invert,
        seed: debouncedParams.seed,
        ornamentStyle: 'floral-wreath',
        symmetry: 'horizontal',
      });
    }
    if (debouncedParams.mode === 'gallery') {
      // Mode gallery : photo courante decodee en ImageData → ascii grid
      // avec whitespaceCutoff 0.92 (cohérent mode marthe — fond clair invisible).
      if (galleryImageData) {
        return imageToAsciiGrid(galleryImageData, {
          glyphSet: debouncedParams.glyphSet,
          density: debouncedParams.density,
          contrast: debouncedParams.contrast,
          brightness: debouncedParams.brightness,
          invert: debouncedParams.invert,
          ditherAmount: debouncedParams.ditherAmount,
          whitespaceCutoff: 0.92,
        });
      }
      return [];
    }
    return generateProceduralAscii({
      glyphSet: debouncedParams.glyphSet,
      density: debouncedParams.density,
      invert: debouncedParams.invert,
      seed: debouncedParams.seed,
      ornamentStyle: debouncedParams.ornamentStyle,
      symmetry: debouncedParams.symmetry,
    });
  }, [
    debouncedParams.mode,
    debouncedParams.glyphSet,
    debouncedParams.density,
    debouncedParams.contrast,
    debouncedParams.brightness,
    debouncedParams.invert,
    debouncedParams.ditherAmount,
    debouncedParams.seed,
    debouncedParams.ornamentStyle,
    debouncedParams.symmetry,
    imageData,
    galleryImageData,
  ]);

  const text = useMemo(() => renderAsciiToString(grid), [grid]);

  /* ---- Refs DOM ---- */
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const scatterCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const particlesCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const preRef = useRef<HTMLPreElement | null>(null);

  /* ---- ParticleSystem (mapping 8 — peace sign) ---- */
  const particleSystemRef = useRef<ParticleSystem | null>(null);
  if (particleSystemRef.current === null) {
    particleSystemRef.current = new ParticleSystem();
  }

  /* ---- Refs hand-tracking lifecycle ---- */
  const trackerRef = useRef<HandTrackerT | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [trackingError, setTrackingError] = useState<string | null>(null);

  /* ---- Refs pour la boucle rAF (lecture O(1), sans re-render) ----
     - handFrameRef : derniere frame MediaPipe (33ms typique)
     - paramsRef    : derniere version des params, pour eviter recreation hook
     - activeRef    : derniere snapshot des gestures actifs (legend)
     - lastSetFrameTsRef : throttle React state a 10 Hz
  */
  const handFrameRef = useRef<HandFrame>(handFrame);
  const paramsRef = useRef<CanvasParams>(params);
  const activeRef = useRef<ActiveGestures>(EMPTY_ACTIVE);
  const [activeGestures, setActiveGestures] = useState<ActiveGestures>(EMPTY_ACTIVE);
  const lastSetFrameTsRef = useRef<number>(0);
  const lastSetActiveTsRef = useRef<number>(0);

  // Garder paramsRef sync avec params (utilise dans la rAF)
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);
  // Garder handFrameRef sync avec handFrame (utilise dans la rAF)
  useEffect(() => {
    handFrameRef.current = handFrame;
  }, [handFrame]);

  /* ---- State persiste de l'animation (mute par rAF, ne re-render pas) ---- */
  const animStateRef = useRef({
    tx: 0,
    ty: 0,
    forwardScale: 1,
    blur: 0,
    scatter: 0, // copy de effectiveScatter pour le canvas overlay
    multiplyCount: 0,
    /** Phase A1 — magnitude vélocité palm (unit/s, lerpé). */
    velocity: 0,
    /** Phase A5 — Z palm landmark (~ -0.1..0.1, lerpé). */
    palmZ: 0,
  });

  /* ---- Phase A1/A5 — palm précédent pour calcul vélocité finite-diff ---- */
  const lastPalmRef = useRef<{ x: number; y: number; ts: number } | null>(null);

  /* ---- Clap gesture (scan CX 2026-05-12 TOP 10 #3) ---- */
  /* Rising-edge detection : true→fire au passage false→true. Cooldown 800ms. */
  const clapPrevRef = useRef<boolean>(false);
  const clapCooldownRef = useRef<number>(0);

  /* ---- Feature flags (resolus au mount, immutables session) ---- */
  const flagsRef = useRef<FeatureFlags>(getFeatureFlagsCached());

  /* ---- Phase B2 — ref vers FluidLayer (impérative API, tick depuis RAF) ---- */
  const fluidLayerRef = useRef<FluidLayerHandle | null>(null);

  /* ---- Mode 'face' — ref vers FaceLayer (impérative API, render autonome) ---- */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const faceLayerRef = useRef<FaceLayerHandle | null>(null);

  /* ---- Mode 'face2' (présence) — ref vers PresenceOverlay ---- */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const presenceOverlayRef = useRef<PresenceOverlayHandle | null>(null);

  /* ---- Phase B6 — frame logger (no-op en prod sauf ?perf=1) ---- */
  const perfLoggerRef = useRef<FrameLogger>(createFrameLogger());

  /* ---- State swipe / scroll right ---- */
  const lastRightXRef = useRef<number | null>(null);
  const lastSwipeTsRef = useRef<number>(0);

  /* ---- Effective scatter (lit handFrame state pour callback grid useMemo).
     La valeur "live" (lue dans la rAF) est calculee dans la boucle. Ce useMemo
     reste pour pre-charger un scatter visuel quand le tracker est OFF mais
     scatterAmount > 0 (slider manuel). */
  const sliderScatter = params.scatterAmount;

  /* ----------------------------------------------------------------------------
     rAF loop : lit handFrameRef, compute mappings, mute DOM directement.
     ---------------------------------------------------------------------------- */
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);

  useEffect(() => {
    let stopped = false;

    const tick = (now: number) => {
      if (stopped) return;
      const dt =
        lastTsRef.current === null
          ? 1 / 60
          : Math.min(0.1, (now - lastTsRef.current) / 1000);
      lastTsRef.current = now;
      perfLoggerRef.current.beginFrame(now);

      const p = paramsRef.current;
      const f = handFrameRef.current;
      const s = animStateRef.current;
      const lerpK = 1 - Math.exp(-LERP_RATE * dt);
      const flags = flagsRef.current;

      // ---------------------------------------------------------------------
      // Phase A1/A5 — Velocity (finite-diff palm) + palm Z, lerpés.
      // Calculés ici (tot) car réutilisés par mappings et drawScatterOverlay.
      // ---------------------------------------------------------------------
      let velocityMag = 0;
      const palm = f.palm;
      if (palm && lastPalmRef.current) {
        const dtPalm = (now - lastPalmRef.current.ts) / 1000;
        if (dtPalm > 0 && dtPalm < 0.5) {
          const dxP = palm.x - lastPalmRef.current.x;
          const dyP = palm.y - lastPalmRef.current.y;
          velocityMag = Math.hypot(dxP, dyP) / dtPalm; // unit/s (normalisé 0..1)
        }
      }
      lastPalmRef.current = palm ? { x: palm.x, y: palm.y, ts: now } : null;
      const velLerpK = 1 - Math.exp(-QW_VELOCITY_LERP * dt);
      s.velocity += (velocityMag - s.velocity) * velLerpK;

      // Z palm landmark (relatif, ~ -0.1..0.1). On prend la 1re main detectée.
      const firstHand = f.landmarks?.[0];
      const palmZRaw = firstHand?.[9]?.z ?? 0;
      const zLerpK = 1 - Math.exp(-QW_PALMZ_LERP * dt);
      s.palmZ += (palmZRaw - s.palmZ) * zLerpK;

      const handTrackingOn = p.handTrackingEnabled;
      const inf = p.handInfluence;
      const landmarks = f.landmarks ?? [];
      const { left, right } = partitionHands(landmarks);
      const handsCount = landmarks.length;

      // --- Mapping 9 : CLAP — cycle glyph set (rising edge + cooldown 800ms) ---
      // Pattern collidingScopes (scan CX 2026-05-12 TOP 10 #3) : geste discret
      // qui change l'état (here : prochain glyph set). View transition wipe
      // automatique grâce à dispatch.
      const clapNow = handTrackingOn && isClap(left, right);
      if (
        clapNow
        && !clapPrevRef.current
        && now - clapCooldownRef.current > 800
      ) {
        const cycle: typeof p.glyphSet[] = [
          'classic', 'dense', 'minimal', 'fairy',
          'symbols', 'blocks', 'cyanotype', 'halftone',
        ];
        const idx = cycle.indexOf(p.glyphSet);
        const next = cycle[(idx + 1) % cycle.length] ?? 'classic';
        dispatch({ type: 'set', key: 'glyphSet', value: next });
        clapCooldownRef.current = now;
      }
      clapPrevRef.current = clapNow;

      // --- Mapping 6 prelim : freeze si fist (any hand) ---
      const fistDetected =
        handTrackingOn && ((left && isFist(left)) || (right && isFist(right))) === true;

      // --- Pinch : exclusion mutuelle entre 3 mappings (1, 3, 4) ---
      // Les 2 pinches serres → SCATTER seulement (mapping 1)
      // Pinch droit SEUL → MULTIPLY (mapping 3)
      // Pinch gauche SEUL → ZOOM forward (mapping 4)
      // Cela rend les gestes non-ambigus et previsibles.
      const PINCH_THRESHOLD = 0.45;
      const pinchR = handTrackingOn && right ? computePinch(right) : 1;
      const pinchL = handTrackingOn && left ? computePinch(left) : 1;
      const pinchedR = handTrackingOn && right !== null && pinchR < PINCH_THRESHOLD;
      const pinchedL = handTrackingOn && left !== null && pinchL < PINCH_THRESHOLD;
      const bothPinched = pinchedR && pinchedL;
      const onlyRightPinched = pinchedR && !pinchedL;
      const onlyLeftPinched = pinchedL && !pinchedR;

      // Mapping 1 : scatter UNIQUEMENT quand les 2 pinches sont serres
      let liveScatter = 0;
      if (bothPinched) {
        const avgPinch = (pinchR + pinchL) / 2;
        liveScatter = Math.min(
          1,
          Math.max(0, (PINCH_THRESHOLD - avgPinch) / PINCH_THRESHOLD),
        );
      }
      const scatterCum = Math.max(
        0,
        Math.min(1, sliderScatter + liveScatter * inf),
      );

      // Mapping 3 : multiply UNIQUEMENT quand pinch droit seul (pas les 2)
      const pinchRightVal: number | null = handTrackingOn && right ? pinchR : null;
      let multiplyCountTarget = 0;
      if (onlyRightPinched) {
        const t = (PINCH_THRESHOLD - pinchR) / PINCH_THRESHOLD;
        multiplyCountTarget = Math.round(t * 5);
      }

      // Mapping 4 : zoom forward UNIQUEMENT quand pinch gauche seul
      const pinchLeftVal: number | null = handTrackingOn && left ? pinchL : null;
      if (onlyLeftPinched) {
        // Pinch gauche actif → vitesse zoom-in proportionnelle a l'intensite
        const t = (PINCH_THRESHOLD - pinchL) / PINCH_THRESHOLD;
        const velocity = t * 0.6 * inf;
        s.forwardScale += velocity * dt;
      } else if (handTrackingOn) {
        // Pas de mapping zoom actif → relachement doux vers 1.0
        s.forwardScale += (1 - s.forwardScale) * 0.05;
      }
      // Clamp [0.5, 3.0]
      s.forwardScale = Math.max(0.5, Math.min(3.0, s.forwardScale));

      // --- Mapping 6 : freeze quand poing ferme - reset doux vers 1 / 0 ---
      if (fistDetected) {
        // Lerp doux vers etat initial
        s.forwardScale += (1 - s.forwardScale) * 0.1;
      }

      // --- Mapping 2 : index pointe → translate sur le tip (override palm) ---
      // --- Mapping 7 : drift sinusoidal quand no hands & tracking ON ---
      let txTarget = 0;
      let tyTarget = 0;
      const canvasW = preRef.current?.parentElement?.clientWidth ?? 800;
      const canvasH = preRef.current?.parentElement?.clientHeight ?? 600;

      const indexPointer = handTrackingOn
        ? pickPointingHand(left, right)
        : null;

      if (handTrackingOn && !fistDetected) {
        if (indexPointer) {
          // Mapping 2 — point sur le tip de l'index (geste explicite)
          const tip = indexPointer[8] as HandLandmark | undefined;
          if (tip) {
            // Mirror X (webcam mirror) : tip.x dans la frame source = cote oppose chez user
            txTarget = (0.5 - tip.x) * 2 * canvasW * 0.4 * inf;
            tyTarget = (tip.y - 0.5) * 2 * canvasH * 0.4 * inf;
          }
        } else if (handsCount === 0) {
          // Mapping 7 — drift sinusoidal quand no hands
          const t = now;
          txTarget = Math.sin(t / 1500) * 12;
          tyTarget = Math.cos(t / 2100) * 10;
        }
        // Sinon (mains ouvertes posees) : pas de translate par defaut. Le
        // dessin reste en position centrale = mode normal.
      }

      // Mapping 6 cont. — freeze : translate target = 0
      if (fistDetected) {
        txTarget = 0;
        tyTarget = 0;
      }

      // --- Mapping 5 : swipe horizontal main droite → cycle ornament ---
      let swipeTriggered = false;
      if (
        handTrackingOn &&
        right &&
        p.mode === 'procedural' &&
        !fistDetected
      ) {
        const cr = palmCentroid(right);
        const last = lastRightXRef.current;
        if (last !== null) {
          const delta = cr.x - last;
          if (
            Math.abs(delta) > SWIPE_DELTA &&
            now - lastSwipeTsRef.current > SWIPE_COOLDOWN_MS
          ) {
            // Direction : mirror webcam → delta>0 dans source = main user qui va a GAUCHE
            // Pour rester intuitif (swipe droite → next), on inverse :
            // delta>0 (source) = swipe gauche user = direction = -1 (previous)
            // delta<0 (source) = swipe droite user = direction = +1 (next)
            const direction = delta > 0 ? -1 : 1;
            const idx = ORNAMENT_CYCLE.indexOf(p.ornamentStyle);
            const nextIdx =
              (idx + direction + ORNAMENT_CYCLE.length) % ORNAMENT_CYCLE.length;
            const next = ORNAMENT_CYCLE[nextIdx]!;
            dispatch({ type: 'set', key: 'ornamentStyle', value: next });
            lastSwipeTsRef.current = now;
            swipeTriggered = true;
          }
        }
        lastRightXRef.current = cr.x;
      } else {
        lastRightXRef.current = null;
      }

      // --- Mapping 8 : Y sign (peace sign) → etoiles magiques ---
      const peaceLeft = handTrackingOn && left ? isPeaceSign(left) : false;
      const peaceRight = handTrackingOn && right ? isPeaceSign(right) : false;
      const peaceDetected = (peaceLeft || peaceRight) && !fistDetected;

      // --- Mapping 9 : finger heart (K-pop) → coeurs flottants ---
      const heartLeft = handTrackingOn && left ? isFingerHeart(left) : false;
      const heartRight = handTrackingOn && right ? isFingerHeart(right) : false;
      const heartDetected = (heartLeft || heartRight) && !fistDetected;

      const ps = particleSystemRef.current;
      if (ps) {
        // Priorite heart > peace si conflit (les 2 sont rares simultanement
        // mais finger heart geste plus precis = priorité)
        if (heartDetected) {
          const handMaking = heartRight ? right : left;
          // Position : mid-point entre thumb (4) et index (8) tips
          const t = handMaking?.[4];
          const i = handMaking?.[8];
          if (t && i) {
            const cw = preRef.current?.parentElement?.clientWidth ?? 800;
            const ch = preRef.current?.parentElement?.clientHeight ?? 600;
            const mx = (t.x + i.x) / 2;
            const my = (t.y + i.y) / 2;
            const ex = (1 - mx) * cw; // mirror X
            const ey = my * ch;
            ps.emitFrom(ex, ey, 'heart');
          }
        } else if (peaceDetected) {
          // Emet depuis la position du tip de l'index de la main qui fait Y.
          // Si les 2 mains font le geste, prioritise droite (main dominante).
          const handMaking = peaceRight ? right : left;
          const tip = handMaking?.[8];
          if (tip) {
            const cw = preRef.current?.parentElement?.clientWidth ?? 800;
            const ch = preRef.current?.parentElement?.clientHeight ?? 600;
            // Mirror X (webcam mirror, voir mapping 2)
            const ex = (1 - tip.x) * cw;
            const ey = tip.y * ch;
            ps.emitFrom(ex, ey, 'star');
          }
        } else {
          ps.stop();
        }
        ps.update(dt);

        // Render dans particlesCanvasRef
        const cv = particlesCanvasRef.current;
        if (cv) {
          const pctx = cv.getContext('2d');
          if (pctx) {
            const dpr = window.devicePixelRatio || 1;
            const rect = cv.getBoundingClientRect();
            if (cv.width !== rect.width * dpr || cv.height !== rect.height * dpr) {
              cv.width = rect.width * dpr;
              cv.height = rect.height * dpr;
            }
            pctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            pctx.clearRect(0, 0, rect.width, rect.height);
            ps.render(pctx);
          }
        }
      }

      // --- Compute blur & scatter target (mapping 1 already done) ---
      // blur subtle quand pinch leger (lecture droit prioritaire, fallback gauche)
      const pinchForBlur =
        pinchRightVal ?? pinchLeftVal ?? (f.pinch as number | null);
      const blurTarget = handTrackingOn
        ? Math.max(
            0,
            (1 - (pinchForBlur ?? 1)) * 0.6 * inf - (fistDetected ? 0.6 : 0),
          )
        : 0;

      // --- LERP framerate-independant ---
      s.tx += (txTarget - s.tx) * lerpK;
      s.ty += (tyTarget - s.ty) * lerpK;
      s.blur += (blurTarget - s.blur) * lerpK;
      s.scatter += (scatterCum - s.scatter) * lerpK;
      // multiplyCount : on lerp un float puis round seulement pour le draw
      s.multiplyCount +=
        ((fistDetected ? 0 : multiplyCountTarget) - s.multiplyCount) * lerpK;

      // --- Mute DOM directement ---
      const pre = preRef.current;
      if (pre) {
        if (!handTrackingOn) {
          // OFF — clear transforms
          pre.style.transform = '';
          pre.style.filter = '';
          pre.style.opacity = '';
        } else {
          const sx = s.forwardScale;
          pre.style.transform = `translate3d(${s.tx.toFixed(2)}px, ${s.ty.toFixed(2)}px, 0) scale(${sx.toFixed(4)})`;
          pre.style.filter =
            s.blur > 0.05 ? `blur(${s.blur.toFixed(2)}px)` : '';
          // Phase A4 — confidence threshold dynamique : confidence haute baisse
          // le seuil (overlay sensible) ; confidence basse = seuil dur (anti-glitch).
          // Si flag OFF : comportement legacy `Math.round(...) > 0`.
          const multiplyOn = flags.quickWinsAlpha
            ? s.multiplyCount > QW_MULTIPLY_BASE_THRESHOLD - QW_MULTIPLY_CONF_BOOST * (f.confidence ?? 0)
            : Math.round(s.multiplyCount) > 0;
          const scatterOn = s.scatter > 0.02;
          pre.style.opacity = multiplyOn || scatterOn ? '0' : '';
        }
      }

      // Phase A3 — pinch droit (0..1) à passer au draw pour decay variation
      const pinchRForDraw = pinchRightVal;

      // --- Redraw scatter / multiply canvas overlay ---
      // Phase A2 — on passe le FLOAT multiplyCount (avant on Math.round'ait).
      drawScatterOverlay(scatterCanvasRef.current, grid, paramsRef.current, {
        scatter: handTrackingOn ? s.scatter : sliderScatter,
        multiplyCount: handTrackingOn ? s.multiplyCount : 0,
        velocity: s.velocity,
        pinchR: handTrackingOn ? pinchRForDraw : null,
        palmZ: s.palmZ,
        confidence: f.confidence ?? 0,
      }, flags);

      // --- Phase B2 — FluidLayer tick (mount conditionnel, RAF unique) ---
      // Pas de second requestAnimationFrame : le FluidLayer délègue son step+
      // render à notre boucle pour préserver le timing du hand tracking.
      // Active si flag fluidSim explicit OU mode 'fluid' (override).
      const fluidActive = flags.fluidSim || p.mode === 'fluid';
      if (fluidActive && fluidLayerRef.current && handTrackingOn) {
        const fluidStart = perfLoggerRef.current.enabled ? performance.now() : 0;
        fluidLayerRef.current.tick(dt, f);
        if (perfLoggerRef.current.enabled) {
          perfLoggerRef.current.sample('fluid', performance.now() - fluidStart);
        }
      }

      // --- Update active gestures snapshot (throttle pour legende) ---
      const noHands = handsCount === 0;
      const newActive: ActiveGestures = {
        scatterFromHands: handTrackingOn && liveScatter > 0.05,
        indexPointing: handTrackingOn && !!indexPointer,
        pinchRightTight:
          handTrackingOn &&
          pinchRightVal !== null &&
          pinchRightVal < 0.4 &&
          !fistDetected,
        pinchLeftTight:
          handTrackingOn &&
          pinchLeftVal !== null &&
          pinchLeftVal < 0.4 &&
          !fistDetected,
        swiping:
          handTrackingOn && (swipeTriggered || now - lastSwipeTsRef.current < 400),
        fist: handTrackingOn && fistDetected,
        noHands: handTrackingOn && noHands,
        peaceSign: peaceDetected,
        fingerHeart: heartDetected,
      };
      activeRef.current = newActive;
      if (now - lastSetActiveTsRef.current > SET_FRAME_THROTTLE_MS) {
        lastSetActiveTsRef.current = now;
        setActiveGestures(newActive);
      }

      perfLoggerRef.current.endFrame(performance.now());
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      stopped = true;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      lastTsRef.current = null;
    };
  }, [grid, sliderScatter, dispatch]);

  /* ---- Reset anim state quand on toggle handTrackingEnabled OFF ---- */
  useEffect(() => {
    if (!params.handTrackingEnabled) {
      const s = animStateRef.current;
      s.tx = 0;
      s.ty = 0;
      s.forwardScale = 1;
      s.blur = 0;
      s.scatter = 0;
      s.multiplyCount = 0;
      lastRightXRef.current = null;
      setActiveGestures(EMPTY_ACTIVE);
    }
  }, [params.handTrackingEnabled]);

  /* ---- Overlay landmarks (webcam preview) — re-dessine sur change handFrame state ---- */
  useEffect(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    if (!params.handTrackingEnabled || !handFrame.landmarks) return;

    const CONNECTIONS: [number, number][] = [
      [0, 1], [1, 2], [2, 3], [3, 4],
      [0, 5], [5, 6], [6, 7], [7, 8],
      [5, 9], [9, 10], [10, 11], [11, 12],
      [9, 13], [13, 14], [14, 15], [15, 16],
      [13, 17], [17, 18], [18, 19], [19, 20],
      [0, 17],
    ];

    for (let h = 0; h < handFrame.landmarks.length; h++) {
      const lms = handFrame.landmarks[h]!;
      const lineColor = h === 0 ? 'rgba(245, 241, 232, 0.85)' : 'rgba(184, 184, 184, 0.85)';
      const dotColor = h === 0 ? 'rgba(245, 241, 232, 0.9)' : 'rgba(184, 184, 184, 0.9)';
      const accentColor = h === 0 ? '#ffffff' : '#dadada';

      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      for (const [a, b] of CONNECTIONS) {
        const pa = lms[a]; const pb = lms[b];
        if (!pa || !pb) continue;
        ctx.moveTo(pa.x * W, pa.y * H);
        ctx.lineTo(pb.x * W, pb.y * H);
      }
      ctx.stroke();

      for (let i = 0; i < lms.length; i++) {
        const p = lms[i];
        if (!p) continue;
        const isAccent = i === 0 || i === 4 || i === 8 || i === 12 || i === 16 || i === 20;
        ctx.fillStyle = isAccent ? accentColor : dotColor;
        ctx.beginPath();
        ctx.arc(p.x * W, p.y * H, isAccent ? 4 : 2.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }, [handFrame, params.handTrackingEnabled]);

  /* ---- Hand tracking lifecycle (webcam + tracker) ---- */
  useEffect(() => {
    let cancelled = false;
    async function startTracking() {
      try {
        setTrackingError(null);
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 320, height: 240, facingMode: 'user' },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play().catch(() => {
          /* play interrupted — ignore */
        });

        // Modes 'face' / 'face2' : on garde le video stream (pour FaceLayer
        // ou PresenceOverlay) mais on ne démarre PAS le HandTracker — ce
        // sont eux qui consomment le video via leur propre detector.
        if (params.mode === 'face' || params.mode === 'face2') {
          return;
        }

        // Dynamic import avec retry — un chunk lazy peut échouer si le user
        // a un cache stale après redeploy (hash chunk change). On retente
        // 2× avec delay avant d'abandonner.
        let HandTrackerClass: typeof import('../../lib/handtracking/mediapipe').HandTracker | null = null;
        let lastImportError: unknown = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          if (cancelled) return;
          try {
            const mod = await import('../../lib/handtracking/mediapipe');
            HandTrackerClass = mod.HandTracker;
            break;
          } catch (err) {
            lastImportError = err;
            const msg = err instanceof Error ? err.message : String(err);
            // Si c'est une erreur fetch (cache stale, network) → retente
            if (/failed to fetch|dynamically imported module|networkerror|chunkloaderror/i.test(msg)
                && attempt < 2) {
              await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
              continue;
            }
            throw err;
          }
        }
        if (!HandTrackerClass) {
          throw lastImportError ?? new Error('hand tracker module load failed');
        }
        if (cancelled) return;
        const { HandTracker } = { HandTracker: HandTrackerClass };
        const tracker = new HandTracker();
        await tracker.init();
        if (cancelled) {
          tracker.dispose();
          return;
        }
        trackerRef.current = tracker;
        tracker.start(video, (f) => {
          // Update ref immediately (toujours, pour la rAF)
          handFrameRef.current = f;
          // Throttle React state setFrame a 10 Hz pour la legend / indicator
          const now = performance.now();
          if (now - lastSetFrameTsRef.current > SET_FRAME_THROTTLE_MS) {
            lastSetFrameTsRef.current = now;
            setFrame(f);
          }
        });
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          setTrackingError(msg);
          dispatch({ type: 'set', key: 'handTrackingEnabled', value: false });
        }
      }
    }

    function stopTracking() {
      const tracker = trackerRef.current;
      if (tracker) {
        tracker.dispose();
        trackerRef.current = null;
      }
      const stream = streamRef.current;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) videoRef.current.srcObject = null;
    }

    if (params.handTrackingEnabled) {
      startTracking();
    } else {
      stopTracking();
    }
    return () => {
      cancelled = true;
      stopTracking();
    };
    // params.mode in deps : switch face↔non-face re-mount le tracker
    // (HandTracker démarré OU non selon le mode actif)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.handTrackingEnabled, params.mode]);

  /* ---- Style fontSize / lineHeight / letterSpacing (transform = mute via rAF) ---- */
  const preStyle = useMemo<CSSProperties>(
    () => ({
      fontSize: params.fontSize,
      lineHeight: params.lineHeight,
      letterSpacing: params.letterSpacing,
    }),
    [params.fontSize, params.lineHeight, params.letterSpacing],
  );

  /* ---- Drop zone fallback ---- */
  const handleFileDrop = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const url = reader.result;
        if (typeof url === 'string') {
          dispatch({ type: 'set', key: 'imageSrc', value: url });
        }
      };
      reader.readAsDataURL(file);
    },
    [dispatch],
  );

  const inputId = 'canvas-fallback-file-input';

  /* ---- Empty state ---- */
  const showEmpty =
    params.mode === 'image-to-ascii' && !params.imageSrc && !decoding;
  const showGalleryEmpty =
    galleryActive && galleryPhotos.length === 0 && !galleryDecoding;

  /* ---- Mode 'fluid' (espace pur Pavel + hand tracking, pas d'ASCII) ---- */
  const fluidMode = fluidActiveMode;
  const fluidLayerActive = flagsRef.current.fluidSim || fluidMode;

  /* ---- Mode 'face' (visage cropped + wireframe mesh + HUD techno) ---- */
  const fuckedUpMode = params.mode === 'fucked-up';
  const faceMode = params.mode === 'face';
  /* ---- Mode 'face2' (présence : vignettes polaroid flottantes) ---- */
  const face2Mode = params.mode === 'face2';
  /* ---- Mode 'fairy-world' (monde ouvert Three.js first-person) ---- */
  const fairyMode = params.mode === 'fairy-world';
  /* ---- Modes vidéo qui désactivent le HandTracker (face/présence
   *  consomment le video sans hand). ---- */
  const videoOnlyMode = faceMode || face2Mode;
  /* ---- Faut-il cacher la base ASCII ? Mode présence + overlay garde
   *  l'ASCII visible derrière les vignettes ; isolated le cache. ---- */
  const hideAsciiBase = fluidMode || faceMode || galleryActive || fuckedUpMode || fairyMode || (face2Mode && params.presenceMode === 'isolated');
  // Exclusion mutuelle face/face2 vs fluid : si on est en face*, fluide ne
  // mount pas même si le flag fluidSim est ON.
  const fluidLayerEffectiveActive = fluidLayerActive && !videoOnlyMode;

  // Toggle hand tracking laissé au contrôle manuel via canvas-hand-toggle
  // (parité avec mode 'procedural'). Si webcam OFF en mode fluid, le canvas
  // reste statique (dernier état du fluide ou vide). Au re-toggle ON, le
  // FluidLayer reprend depuis l'état persistant.
  //
  // Exception mode 'face2' (présence) : auto-active la webcam au switch.
  // Le mode présence est inutilisable sans webcam (rien à détecter), donc
  // on déclenche la perm caméra dès l'entrée. Le user reste libre de la
  // désactiver via le toggle après.
  const prevModeRef = useRef<typeof params.mode>(params.mode);
  useEffect(() => {
    const prev = prevModeRef.current;
    if (params.mode === 'face2' && prev !== 'face2' && !params.handTrackingEnabled) {
      dispatch({ type: 'set', key: 'handTrackingEnabled', value: true });
    }
    prevModeRef.current = params.mode;
  }, [params.mode, params.handTrackingEnabled, dispatch]);

  /* ---- Chat → fluid : nouveau message verified = perturbation poétique.
     Track ids vus, splat injectMessage seulement sur new arrivals. Le set
     est initialisé au mount avec les messages déjà présents pour ne pas
     splatter tout le backlog d'un coup. */
  const seenMessageIdsRef = useRef<Set<string>>(new Set());
  const seenInitializedRef = useRef(false);
  useEffect(() => {
    if (!fluidMode) {
      // Quitte le mode → reset le tracking (au re-entry, on re-prime avec backlog)
      seenInitializedRef.current = false;
      seenMessageIdsRef.current.clear();
      return;
    }
    const verified = polled.messages.filter((m) => m.verified);
    if (!seenInitializedRef.current) {
      // Prime initiale : marque tous les existants comme vus, ne splat que sur
      // les arrivées vraiment nouvelles à partir de maintenant.
      for (const m of verified) seenMessageIdsRef.current.add(m.id);
      seenInitializedRef.current = true;
      return;
    }
    const fluid = fluidLayerRef.current;
    if (!fluid) return;
    for (const m of verified) {
      if (seenMessageIdsRef.current.has(m.id)) continue;
      seenMessageIdsRef.current.add(m.id);
      fluid.injectMessage(m);
    }
  }, [fluidMode, polled.messages]);

  /* ---- Build root classes ---- */
  const bgClass = bgClassFor(params);
  const fgClass = fgClassFor(params);

  /* ---- Scatter overlay flag pour CSS visibility ---- */
  // En mode fluid : pas de scatter du tout, c'est un espace fluide pur.
  const overlayVisible =
    !fluidMode &&
    (params.scatterAmount > 0.02 ||
      (params.handTrackingEnabled &&
        (activeGestures.scatterFromHands || activeGestures.pinchRightTight)));

  return (
    <div className={`canvas-root ${bgClass}`} data-mode={params.mode}>
      {/* Webcam preview — visible bottom-right when hand tracking actif.
          Cachée en mode 'face' / 'face2' / 'fluid' : ces modes consomment
          déjà le video stream (FaceLayer / vignettes / fluid splats) et
          la preview serait redondante / bruyante. Le <video> reste mounté
          (les trackers en ont besoin) mais opacity 0 + size 1px via CSS
          quand la classe --on est absente. */}
      <div
        className={`canvas-video-wrap ${
          params.handTrackingEnabled && !faceMode && !face2Mode && !fluidMode && !galleryActive && !fairyMode
            ? 'canvas-video-wrap--on'
            : ''
        }`}
      >
        <video
          ref={videoRef}
          className="canvas-video"
          muted
          playsInline
          aria-label="Retour camera (hand tracking)"
        />
        <canvas
          ref={overlayCanvasRef}
          width={320}
          height={240}
          className="canvas-video-overlay"
          aria-hidden="true"
        />
      </div>

      {/* Hand tracking toggle button (top-right) — masqué en mode gallery
          (pas de geste utile sur la grille de photos brutes) et en mode
          fairy-world (pointer-lock incompatible avec le hand tracking). */}
      {!galleryActive && !fairyMode && (
      <button
        type="button"
        className={`canvas-hand-toggle ${
          params.handTrackingEnabled ? 'canvas-hand-toggle--on' : ''
        } ${
          params.handTrackingEnabled && handFrame.palm
            ? 'canvas-hand-toggle--active'
            : ''
        }`}
        onClick={() =>
          dispatch({
            type: 'set',
            key: 'handTrackingEnabled',
            value: !params.handTrackingEnabled,
          })
        }
        aria-pressed={params.handTrackingEnabled}
        title={
          params.handTrackingEnabled
            ? 'desactiver le hand tracking'
            : 'activer le hand tracking (permission webcam requise)'
        }
      >
        <span className="canvas-hand-toggle__dot" aria-hidden="true" />
        <span>{params.handTrackingEnabled ? 'hand · on' : 'hand · off'}</span>
      </button>
      )}

      {/* Decoding state */}
      {(decoding || (galleryActive && galleryDecoding)) && (
        <div className="canvas-loading">decoding…</div>
      )}

      {/* Decode error */}
      {decodeError && !decoding && (
        <div className="canvas-empty">
          <span>decode error</span>
          <span style={{ color: 'var(--fg-faint)', fontSize: 11 }}>
            {decodeError}
          </span>
        </div>
      )}

      {/* Gallery decode error */}
      {galleryActive && galleryDecodeError && !galleryDecoding && (
        <div className="canvas-empty">
          <span>photo decode error</span>
          <span style={{ color: 'var(--fg-faint)', fontSize: 11 }}>
            {galleryDecodeError}
          </span>
        </div>
      )}

      {/* Gallery empty state */}
      {showGalleryEmpty && (
        <div className="canvas-empty" data-testid="gallery-empty">
          <p style={{ margin: 0 }}>aucune photo reçue pour l'instant</p>
          <p style={{ margin: 0, color: 'var(--fg-faint)', fontSize: 11 }}>
            partage le lien /send avec tes amis
          </p>
          <a
            href="/send"
            className="canvas-empty__cta"
            style={{ textDecoration: 'none' }}
          >
            ouvrir /send →
          </a>
        </div>
      )}

      {/* Tracking error — affiche un bouton 'rafraîchir' si l'erreur ressemble
          à un chunk-load failure (cache navigateur stale post-deploy). */}
      {trackingError && (
        <div
          className="canvas-loading"
          style={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
          }}
        >
          <span>tracking off — {trackingError.slice(0, 64)}</span>
          {/failed to fetch|dynamically imported module|networkerror|chunkloaderror/i.test(trackingError) && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                fontFamily: 'inherit',
                fontSize: 11,
                padding: '4px 10px',
                background: 'transparent',
                border: '1px solid currentColor',
                color: 'inherit',
                cursor: 'pointer',
                letterSpacing: '0.06em',
              }}
            >
              ↻ rafraîchir
            </button>
          )}
        </div>
      )}

      {/* Empty state (image-to-ascii sans image) */}
      {showEmpty && (
        <div className="canvas-empty">
          <p style={{ margin: 0 }}>drop une image dans le panneau de gauche</p>
          <p style={{ margin: 0, color: 'var(--fg-faint)', fontSize: 11 }}>
            ou switch en mode procedural
          </p>
          <input
            id={inputId}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileDrop(file);
            }}
          />
          <label htmlFor={inputId} className="canvas-empty__cta">
            choose image →
          </label>
          <button
            type="button"
            className="canvas-empty__cta"
            onClick={() =>
              dispatch({ type: 'set', key: 'mode', value: 'procedural' })
            }
          >
            switch procedural →
          </button>
        </div>
      )}

      {/* Grid render — <pre> par défaut, fade out quand scatter / multiply actif.
          Skip si hideAsciiBase (modes fluid / face / face2 isolated). En mode
          face2 overlay, garde l'ASCII visible derrière les vignettes.
          Quand glyphSet='cyanotype' → le <pre> est invisible (mais reste pour
          export SVG) et le StippleLayer ci-dessous prend le visuel. */}
      {!hideAsciiBase && !showEmpty && !showGalleryEmpty && grid.length > 0 && (
        <pre
          ref={preRef}
          className={`canvas-pre ${fgClass} ${(params.glyphSet === 'cyanotype' || params.glyphSet === 'halftone' || params.glyphSet === 'liquid') ? 'canvas-pre--hidden' : ''}`}
          style={preStyle}
          data-testid="canvas-ascii"
        >
          {text}
        </pre>
      )}

      {/* Stipple renderer (canvas 2D cercles jitterisés) — pour cyanotype.
          Pointillisme aléatoire, fond bleu de Prusse. */}
      {!hideAsciiBase && !showEmpty && !showGalleryEmpty && grid.length > 0 && params.glyphSet === 'cyanotype' && (
        <StippleLayer grid={grid} params={params} />
      )}

      {/* Halftone renderer (canvas 2D cercles alignés grille) — pour halftone.
          Cercles centrés dans chaque cellule, radius modulé par luminance.
          Respecte palette user (grayscale doctrine). */}
      {!hideAsciiBase && !showEmpty && !showGalleryEmpty && grid.length > 0 && params.glyphSet === 'halftone' && (
        <HalftoneLayer grid={grid} params={params} />
      )}

      {/* Liquid renderer (canvas 2D + RAF) — pour glyph set 'liquid'.
          ASCII animé avec wave continue, inspiré Unseen Studio 2025 Wrapped. */}
      {!hideAsciiBase && !showEmpty && !showGalleryEmpty && grid.length > 0 && params.glyphSet === 'liquid' && (
        <LiquidLayer grid={grid} params={params} />
      )}


      {/* Mode 'gallery' — grille brute de toutes les photos (R2 src direct,
          pas de conversion ASCII, pas de carrousel). */}
      {galleryActive && <GalleryGrid messages={polled.messages} />}

      {/* Mode 'fucked up' — chaos contrôlé : messages texte + photos R2
          apparaissent à positions aléatoires + opacity fade cyclique,
          rotation jitter, glitch RGB burst au peak. */}
      {fuckedUpMode && (
        <FuckedUpLayer
          messages={polled.messages.filter((m) => m.verified)}
          params={params}
        />
      )}

      {/* Mode 'fairy-world' — monde ouvert Three.js first-person.
          Forêt nocturne féerique : terrain PBR, lucioles, champignons
          bioluminescents, wisps, bloom. Lazy chunk (three.js). */}
      {fairyMode && (
        <Suspense fallback={null}>
          <FairyWorldLayer
            quality={params.fairyQuality}
            messages={polled.messages
              .filter((m) => m.verified)
              .map((m) => ({ title: m.title, body: m.body }))}
          />
        </Suspense>
      )}

      {/* Mode 'face' — FaceLayer overlay (lazy chunk MediaPipe FaceLandmarker
          ~3.7MB modèle). Mount uniquement si mode='face'. */}
      {faceMode && (
        <Suspense fallback={null}>
          <FaceLayer
            ref={faceLayerRef}
            videoRef={videoRef}
            enabled={params.handTrackingEnabled}
            palette={params.palette}
            bgPalette={params.bgPalette}
          />
        </Suspense>
      )}

      {/* Mode 'face2' (présence) — PresenceOverlay (vignettes polaroid
          flottantes, FaceDetector multi-faces). Mount uniquement si
          mode='face2'. visualMode `isolated` (default) = fond noir,
          `overlay` = transparent par-dessus glyph art. */}
      {face2Mode && (
        <Suspense fallback={null}>
          <PresenceOverlay
            ref={presenceOverlayRef}
            videoRef={videoRef}
            enabled={params.handTrackingEnabled}
            visualMode={params.presenceMode}
            freeze={params.presenceFreeze}
            styles={params.presenceStyles}
          />
        </Suspense>
      )}


      {/* Phase B2 — FluidLayer overlay (lazy, mount conditionnel).
          Activé si flag fluidSim explicit OU mode 'fluid' (override).
          Exclu en mode 'face' (exclusion mutuelle).
          Suspense fallback null (UX : transition silencieuse pendant chargement
          du chunk Pavel ~5KB gzip). Si WebGL2 indispo, FluidLayer rend null
          en interne → fallback gracieux vers Phase A scatter. */}
      {fluidLayerEffectiveActive && (
        <Suspense fallback={null}>
          <FluidLayer
            ref={fluidLayerRef}
            solo={fluidMode}
            palette={params.palette}
            bgPalette={params.bgPalette}
          />
        </Suspense>
      )}

      {/* Particles canvas overlay (mapping 8 — peace sign etoiles).
          Désactivé en mode fluid / face / face2. */}
      {!hideAsciiBase && (
        <canvas
          ref={particlesCanvasRef}
          className="canvas-particles"
          aria-hidden="true"
        />
      )}

      {/* Scatter / Multiply canvas overlay (uniquement modes ASCII). */}
      {!hideAsciiBase && (
        <canvas
          ref={scatterCanvasRef}
          className={`canvas-scatter ${overlayVisible ? 'canvas-scatter--on' : ''}`}
          data-overlay-active={overlayVisible ? 'true' : 'false'}
          aria-hidden="true"
        />
      )}

      {/* Hand tracking shortcuts legend (bottom-left) — 7 mappings, mise en
          evidence visuelle quand le geste est actif. Cachés en mode 'face' /
          'face2' / 'fluid' : ces modes ne mappent pas les 7 gestes hand
          (face* sans hand tracking, fluid utilise les mains pour splat
          fluide direct sans cycle ornament/scatter/etc). */}
      {params.handTrackingEnabled && !faceMode && !face2Mode && !fluidMode && !galleryActive && (
        <div className="canvas-shortcuts" aria-hidden="true">
          <Shortcut
            icon="◐◑"
            label="pinch 2 mains → scatter"
            active={activeGestures.scatterFromHands}
          />
          <Shortcut
            icon="☝"
            label="index pointe → suivre le doigt"
            active={activeGestures.indexPointing}
          />
          <Shortcut
            icon="✦◀"
            label="pinch droit → multiplier"
            active={activeGestures.pinchRightTight}
          />
          <Shortcut
            icon="✦▶"
            label="pinch gauche → zoom avant"
            active={activeGestures.pinchLeftTight}
          />
          <Shortcut
            icon="≫"
            label="swipe → ornement"
            active={activeGestures.swiping}
          />
          <Shortcut
            icon="✊"
            label="poing ferme → freeze"
            active={activeGestures.fist}
          />
          <Shortcut
            icon="∼"
            label="no hands → drift"
            active={activeGestures.noHands}
          />
          <Shortcut
            icon="✌"
            label="Y sign → etoiles magiques"
            active={activeGestures.peaceSign}
          />
          <Shortcut
            icon="♡"
            label="finger heart → coeurs flottants"
            active={activeGestures.fingerHeart}
          />
        </div>
      )}

      {/* Gallery mini nav (bas-centre) — affiche position + prev/next + pause */}
      {/* Nav prev/next/counter — DÉSACTIVÉE pour le nouveau mode galerie grille
          (toutes les photos s'affichent sur la même page). Conservé en false
          fixé pour ne pas casser le DOM testé existant. */}
      {false && galleryActive && galleryPhotos.length > 0 && (
        <div className="canvas-gallery-nav" data-testid="canvas-gallery-nav">
          <button
            type="button"
            className="canvas-gallery-nav__btn"
            onClick={() => onGalleryCommand({ cmd: 'prev' })}
            aria-label="photo precedente"
            title="precedent"
          >
            ←
          </button>
          <span
            className="canvas-gallery-nav__counter"
            data-testid="canvas-gallery-counter"
          >
            {galleryIndex + 1}/{galleryPhotos.length}
          </span>
          <button
            type="button"
            className="canvas-gallery-nav__btn"
            onClick={() => onGalleryCommand({ cmd: 'next' })}
            aria-label="photo suivante"
            title="suivant"
          >
            →
          </button>
          <button
            type="button"
            className="canvas-gallery-nav__btn canvas-gallery-nav__btn--toggle"
            onClick={() =>
              dispatch({
                type: 'set',
                key: 'galleryAutoRotateMs',
                value: params.galleryAutoRotateMs === 0 ? 6000 : 0,
              })
            }
            aria-label={
              params.galleryAutoRotateMs === 0
                ? 'reprendre le carousel'
                : 'mettre en pause le carousel'
            }
            title={
              params.galleryAutoRotateMs === 0
                ? 'play'
                : `pause (${params.galleryAutoRotateMs / 1000}s)`
            }
            data-testid="canvas-gallery-toggle"
          >
            {params.galleryAutoRotateMs === 0 ? '▶' : '∥'}
          </button>
        </div>
      )}

      {/* Action bar bottom (reset + downloads) — masquée en fairy-world
          (exports ASCII sans objet dans le monde 3D, et la barre passerait
          sous le pointer-lock). */}
      {!fairyMode && (
      <div className="canvas-actions">
        <button
          type="button"
          className="canvas-action"
          onClick={() => dispatch({ type: 'reset' })}
          data-testid="reset-button"
          title="reset defaults"
        >
          ↺ reset
        </button>
        <button
          type="button"
          className="canvas-action"
          onClick={() => downloadAsciiAsSvg(text, params)}
          data-testid="download-svg-button"
          disabled={!text}
          title="download SVG"
        >
          ↓ svg
        </button>
        <button
          type="button"
          className="canvas-action"
          onClick={() => downloadAsciiAsPng(text, params)}
          data-testid="download-png-button"
          disabled={!text}
          title="download PNG"
        >
          ↓ png
        </button>
        <button
          type="button"
          className="canvas-action"
          onClick={() => navigator.clipboard.writeText(text).catch(() => {})}
          disabled={!text}
          title="copy ASCII"
        >
          ⧉ copy
        </button>
      </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------------
   Shortcut sub-component — ligne legende
   ---------------------------------------------------------------------------- */

function Shortcut({
  icon,
  label,
  active,
}: {
  icon: string;
  label: string;
  active: boolean;
}) {
  return (
    <div className={`canvas-shortcut ${active ? 'canvas-shortcut--active' : ''}`}>
      <span className="canvas-shortcut__icon">{icon}</span>
      <span className="canvas-shortcut__label">{label}</span>
    </div>
  );
}

/* ----------------------------------------------------------------------------
   pickPointingHand — retourne la main qui pointe (l'index est etendu, autres
   plies). Si les deux pointent : prioritise droite. Si aucune : null.
   ---------------------------------------------------------------------------- */

function pickPointingHand(
  left: HandLandmark[] | null,
  right: HandLandmark[] | null,
): HandLandmark[] | null {
  if (right && isIndexPointing(right)) return right;
  if (left && isIndexPointing(left)) return left;
  return null;
}

/* ----------------------------------------------------------------------------
   drawScatterOverlay — dessine sur le canvas overlay :
     - scatter : chaque glyph avec jitter seede
     - multiply : N copies du grid translate (pas de jitter par glyph)
   Les 2 sont rendus combines : scatter sur la copie 0, copies additionnelles
   translateees seulement (pas re-jittered).
   ---------------------------------------------------------------------------- */

interface ScatterOverlayState {
  scatter: number;
  /** FLOAT 0..5 — Phase A2 (avant : int Math.round). */
  multiplyCount: number;
  /** Phase A1 — magnitude vélocité palm normalisée [unit/s]. */
  velocity: number;
  /** Phase A3 — pinch droit 0..1, null si main droite absente. */
  pinchR: number | null;
  /** Phase A5 — Z palm lerpé. */
  palmZ: number;
  /** Confidence 0..1 (pour gating decay/A3). */
  confidence: number;
}

function drawScatterOverlay(
  cvs: HTMLCanvasElement | null,
  grid: string[][],
  params: CanvasParams,
  state: ScatterOverlayState,
  flags: FeatureFlags,
) {
  if (!cvs) return;
  const ctx = cvs.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const rect = cvs.getBoundingClientRect();
  if (cvs.width !== rect.width * dpr || cvs.height !== rect.height * dpr) {
    cvs.width = rect.width * dpr;
    cvs.height = rect.height * dpr;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, rect.width, rect.height);

  const { scatter, multiplyCount, velocity, pinchR, palmZ, confidence } = state;
  // Phase A2 — seuil minuscule (epsilon float) au lieu de "=== 0" pour éviter
  // un cutoff abrupt à la fin du fade-out. <=0.05 ≈ alpha < 0.05 (invisible).
  if ((scatter < 0.02 && multiplyCount <= 0.05) || grid.length === 0) return;

  const fs = params.fontSize;
  const charW = fs * 0.6 + params.letterSpacing;
  const lh = fs * params.lineHeight;
  const cols = grid[0]?.length ?? 0;
  const rows = grid.length;

  const blockW = cols * charW;
  const blockH = rows * lh;
  const ox = (rect.width - blockW) / 2;
  const oy = (rect.height - blockH) / 2;

  // Modes dot-based : cyanotype et halftone basculent en rendu cercle au lieu
  // de fillText. Cyanotype override la palette (blanc cassé pour cohérence
  // bleu/blanc), halftone respecte la palette utilisateur (grayscale doctrine).
  const isCyano = params.glyphSet === 'cyanotype';
  const isHalftone = params.glyphSet === 'halftone';
  const isDotMode = isCyano || isHalftone;
  const DOT_SET_LOCAL = ' ·°∘∙◦○⊙◉●'; // identique pour cyano et halftone
  const DOT_BASE_R = fs * 0.22;

  ctx.font = `${fs}px "JetBrains Mono", ui-monospace, monospace`;
  ctx.textBaseline = 'top';
  let fill = paletteToHex(params.palette);
  if (params.bgPalette === 'ink' && params.palette === 'mono-ink') {
    fill = '#ffffff';
  }
  if (isCyano) {
    fill = '#F4F7FB'; // blanc cassé cyanotype, override palette
  }
  ctx.fillStyle = fill;

  /** Dessine un char OU un dot selon le glyph set actif. */
  const drawGlyph = (ch: string, x: number, y: number) => {
    if (isDotMode) {
      const idx = DOT_SET_LOCAL.indexOf(ch);
      if (idx <= 0) return;
      const density = idx / (DOT_SET_LOCAL.length - 1);
      const radius = DOT_BASE_R * (0.35 + 0.65 * density);
      ctx.beginPath();
      // x, y représentent le coin haut-gauche de la cellule (comme fillText
      // baseline top) — on offset par charW/2 et lh/2 pour centrer le dot
      ctx.arc(x + charW / 2, y + lh / 2, radius, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.fillText(ch, x, y);
    }
  };

  const maxR = Math.min(rect.width, rect.height) * 0.5 * scatter;

  // PRNG seede deterministe
  const baseSeed = (params.seed * 0x9e3779b9) >>> 0;
  const makeRng = (s: number) => {
    let seed = s >>> 0;
    return () => {
      seed |= 0;
      seed = (seed + 0x6d2b79f5) | 0;
      let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };

  // ---- Copie 0 : grid normal + scatter (si scatter actif) ----
  const baseRng = makeRng(baseSeed);
  for (let r = 0; r < rows; r++) {
    const row = grid[r]!;
    for (let c = 0; c < cols; c++) {
      const ch = row[c];
      if (!ch || ch === ' ') continue;
      const baseX = ox + c * charW;
      const baseY = oy + r * lh;
      const jx = scatter > 0.02 ? (baseRng() - 0.5) * 2 * maxR : 0;
      const jy = scatter > 0.02 ? (baseRng() - 0.5) * 2 * maxR : 0;
      drawGlyph(ch, baseX + jx, baseY + jy);
    }
  }

  // ---- Copies additionnelles (mapping 3 — multiply) ----
  // Chaque copie est translate avec un offset seede distinctement. Pas de
  // jitter par glyph (juste translate du grid entier) pour effet "fantome".
  //
  // Quick Wins Phase A — modulations dynamiques (legacy comportement preserve
  // si flags.quickWinsAlpha = false, A5 separe via flags.quickWinsZ).
  //   A1 — alpha de base modulé par vélocité main (gestes rapides plus marqués)
  //   A2 — boucle FLOAT : i ∈ [1..ceil(count)], alpha_i fractionnel pour
  //        smooth fade-in/out aux transitions de palier (avant : saut à 0.5)
  //   A3 — decay variation par pinch droit (pinch fermé = decay plat = encre
  //        "lourde" ; pinch ouvert = decay aggressif = encre "éclate")
  //   A5 — parallax Z : copies se déplient en profondeur via palmZ landmark
  if (multiplyCount > 0.05) {
    // Pour amplitude on utilise le float multiplyCount tel quel (l'amplitude
    // varie smoothly avec le pinch).
    const mcClamped = Math.min(5, multiplyCount);
    const pinchProxy = 1 - mcClamped / 5; // 0..1, 0=ferme
    const amp = (0.15 + pinchProxy * 0.3) * Math.min(rect.width, rect.height);

    // ----- A1 — alpha de base modulé par vélocité -----
    let baseAlpha = QW_BASE_ALPHA;
    if (flags.quickWinsAlpha) {
      const velNorm = Math.min(1, velocity / QW_MAX_VELOCITY);
      baseAlpha = QW_BASE_ALPHA + QW_ALPHA_BOOST * velNorm;
      // Clamp explicite au cas où velocity > QW_MAX_VELOCITY (jitter)
      baseAlpha = Math.max(QW_BASE_ALPHA, Math.min(QW_BASE_ALPHA + QW_ALPHA_BOOST, baseAlpha));
    }

    // ----- A3 — pinch ratio pour decay (gating par confidence anti-glitch) -----
    // pinchR proche 0 = ferme → decay plat (encre lourde)
    // pinchR proche 1 = ouvert → decay agressif (encre éclate)
    const pinchRatio = (flags.quickWinsAlpha && pinchR !== null && confidence >= 0.5)
      ? Math.pow(Math.max(0, Math.min(1, pinchR)), 0.7)
      : 1;

    // ----- A5 — parallax Z (offset linéaire par index copie) -----
    // palmZ MediaPipe est ~ -0.1..0.1 en pratique, donc * 100 → ~ -10..10 px
    // multiplié ensuite par (i-1) pour effet "dépliage" en profondeur.
    const zBiasX = flags.quickWinsZ ? palmZ * QW_PALMZ_PX : 0;

    // ----- A2 — boucle float : ceil(count) copies, alpha fractionnel sur la dernière -----
    // Distribution radiale (2026-05-12) : chaque copie s'éloigne du centre du
    // canvas, magnitude croissante avec l'index. Angles répartis via golden
    // angle (≈137.5°, phyllotaxie tournesols) + petit jitter par seed pour
    // équirépartition naturelle sur le cercle sans alignements stricts.
    const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
    const totalCopies = Math.ceil(multiplyCount);
    for (let i = 1; i <= totalCopies; i++) {
      // alpha fractionnel : copies 1..floor(count) ont alpha_i=1, dernière copie
      // a alpha_i = frac(count). Smooth fade aux transitions de palier.
      const alphaI = Math.max(0, Math.min(1, multiplyCount - i + 1));
      // A3 : decay variation per-copie modulé par pinch
      const decayI = flags.quickWinsAlpha
        ? 1 - ((i - 1) / 5) * 0.5 * pinchRatio
        : 1;
      ctx.globalAlpha = baseAlpha * alphaI * decayI;

      const seedI = (baseSeed ^ (i * 0x9e3779b9)) >>> 0;
      const rng = makeRng(seedI);
      // Angle = golden angle pour i + jitter ±0.2 rad (~11°) pour casser
      // l'alignement strict tout en conservant la dispersion équilibrée.
      const angleI = (i - 1) * GOLDEN_ANGLE + (rng() - 0.5) * 0.4;
      // Distance radiale : croît linéairement de 1/N à 1.0 (copie 1 = la
      // plus proche du centre, copie N = la plus éloignée).
      const radialFactor = i / totalCopies;
      const distI = radialFactor * amp;
      const dx = Math.cos(angleI) * distI + zBiasX * (i - 1);
      const dy = Math.sin(angleI) * distI;
      for (let r = 0; r < rows; r++) {
        const row = grid[r]!;
        for (let c = 0; c < cols; c++) {
          const ch = row[c];
          if (!ch || ch === ' ') continue;
          drawGlyph(ch, ox + c * charW + dx, oy + r * lh + dy);
        }
      }
    }
    ctx.globalAlpha = 1;
  }
}

/* ----------------------------------------------------------------------------
   Download helpers (SVG + PNG) — derived from the ASCII text
   ---------------------------------------------------------------------------- */

function downloadAsciiAsSvg(text: string, p: CanvasParams) {
  if (!text) return;
  const lines = text.split('\n');
  const cols = lines.reduce((m, l) => Math.max(m, l.length), 0);
  const rows = lines.length;
  const fs = p.fontSize;
  const charW = fs * 0.6 + p.letterSpacing;
  const lh = fs * p.lineHeight;
  const W = Math.ceil(cols * charW) + 32;
  const H = Math.ceil(rows * lh) + 32;
  const fg = paletteToHex(p.palette);
  const bg = bgToHex(p.bgPalette);
  const escaped = lines.map((l) => l.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'));
  const tspans = escaped
    .map((l, i) => `<tspan x="16" dy="${i === 0 ? lh : lh}">${l}</tspan>`)
    .join('');
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="100%" height="100%" fill="${bg}"/>
  <text x="16" y="0" fill="${fg}" font-family="JetBrains Mono, ui-monospace, monospace" font-size="${fs}" letter-spacing="${p.letterSpacing}px" xml:space="preserve">${tspans}</text>
</svg>`;
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  triggerDownload(blob, `marthe-ascii-${Date.now()}.svg`);
}

function downloadAsciiAsPng(text: string, p: CanvasParams) {
  if (!text) return;
  const lines = text.split('\n');
  const cols = lines.reduce((m, l) => Math.max(m, l.length), 0);
  const rows = lines.length;
  const fs = p.fontSize;
  const charW = fs * 0.6 + p.letterSpacing;
  const lh = fs * p.lineHeight;
  const scale = 2;
  const W = Math.ceil(cols * charW) + 32;
  const H = Math.ceil(rows * lh) + 32;
  const canvas = document.createElement('canvas');
  canvas.width = W * scale;
  canvas.height = H * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(scale, scale);
  ctx.fillStyle = bgToHex(p.bgPalette);
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = paletteToHex(p.palette);
  ctx.font = `${fs}px "JetBrains Mono", ui-monospace, monospace`;
  ctx.textBaseline = 'top';
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i] ?? '', 16, 16 + i * lh);
  }
  canvas.toBlob((blob) => {
    if (blob) triggerDownload(blob, `marthe-ascii-${Date.now()}.png`);
  }, 'image/png');
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function paletteToHex(id: CanvasParams['palette']): string {
  switch (id) {
    case 'mono-ink': return '#0d0d0d';
    case 'silver': return '#b8b8b8';
    case 'graphite': return '#2c2c2c';
    case 'stone': return '#8a8a8a';
    case 'ash': return '#5a5a5a';
    default: return '#0d0d0d';
  }
}

function bgToHex(id: CanvasParams['bgPalette']): string {
  switch (id) {
    case 'paper': return '#ffffff';
    case 'ink': return '#0d0d0d';
    default: return '#ffffff';
  }
}

/* ----------------------------------------------------------------------------
   Helpers — class name lookups (evite les inline styles couleur)
   ---------------------------------------------------------------------------- */

function bgClassFor(p: CanvasParams): string {
  switch (p.bgPalette) {
    case 'paper':
      return 'canvas-bg-paper';
    case 'ink':
      return 'canvas-bg-ink';
    default:
      return 'canvas-bg-paper';
  }
}

function fgClassFor(p: CanvasParams): string {
  switch (p.palette) {
    case 'mono-ink':
      return 'canvas-fg-mono-ink';
    case 'silver':
      return 'canvas-fg-silver';
    case 'graphite':
      return 'canvas-fg-graphite';
    case 'stone':
      return 'canvas-fg-stone';
    case 'ash':
      return 'canvas-fg-ash';
    default:
      return 'canvas-fg-mono-ink';
  }
}
